import { useDispatch } from 'react-redux'
import { useAsync } from 'react-async-hook'
import Button from '@mui/material/Button'

import { ListUserSortableField, Role } from 'common/api/v1/types'
import { isEditableGroup, usePageParamsFiltered, useUser, useConfirmationDialog } from '../../utils'
import Table, { TableConfig } from '../common/Table'
import { MissingContent } from '../common/MissingContent'
import Wrapper from '../common/Wrapper'
import { Link } from '../common/Link'
import { EnrichedUser, UsersRequestParams } from '../../api/nm-types'
import { getUsers } from '../../redux/actions/userActions'
import { Api, AppDispatch, useRoutes } from '../../store'
import { FailedFetchingContent } from '../common/FailedFetchingContent'
import Toolbar from '@mui/material/Toolbar'
import {
  ListUserFilterKey,
  makeListUsersFilter,
  mapUrlParamToUserFilter,
  mapUserFilterToUrlParam,
} from './listUserFilter'
import { UrlParamFilteredSearchBar } from '../common/Filters/FilterView/FilteredSearchBar'
import DeleteIcon from '@mui/icons-material/Delete'
import SettingsIcon from '@mui/icons-material/Settings'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'
import { ActionMenu } from '../common/Table/ActionMenu'
import { impersonateUser, removeUser } from '../../redux/actions/userActions'
import { useNavigate } from 'react-router'

const { userApi } = Api

const RoleNum = Object.keys(Role).reduce(
  (a, c, i) => {
    return { ...a, [c]: i }
  },
  {} as Record<Role, number>,
)

export const ListView = () => {
  const routes = useRoutes()
  const dispatch = useDispatch<AppDispatch>()
  const searchParams = usePageParamsFiltered<UsersRequestParams>()
  const {
    result: users,
    loading,
    error,
    execute: refreshUsers,
  } = useAsync(() => getUsers(searchParams, { dispatch, userApi }), [JSON.stringify(searchParams)])
  const showConfirmation = useConfirmationDialog()
  const navigate = useNavigate()

  const currentUser = useUser()

  const canEdit = (user: EnrichedUser) =>
    currentUser.role === Role.super ||
    (currentUser.role === Role.admin && currentUser.group === user.group && user.role !== Role.super)

  const canImpersonate = (userToImpersonate: EnrichedUser) => {
    if (userToImpersonate.id === currentUser.id) return false
    if (currentUser.role !== Role.super && userToImpersonate.group !== currentUser.group) return false
    return RoleNum[userToImpersonate.role] < RoleNum[currentUser.role]
  }

  const showImpersonationDialog = useConfirmationDialog()
  const onImpersonateButtonClicked = (user: EnrichedUser) =>
    showImpersonationDialog(
      () => void dispatch(impersonateUser(user.id)),
      <>
        Do you want to log in as <strong>{user.username}</strong>?
        <br />
        You will be logged out from your current session.
      </>,
    )

  const tableConfig: TableConfig<EnrichedUser, ListUserSortableField> = [
    {
      title: 'name',
      getValue: (user) => (
        <Link available={canEdit(user)} to={routes.usersUpdate({ id: user.id })} underline="hover">
          {user.username}
        </Link>
      ),
      sorting: { byParameter: ListUserSortableField.userName },
    },
    {
      title: 'group',
      getValue: ({ _group }) => (
        <Link
          to={routes.groupsUpdate({ id: _group.id })}
          underline="hover"
          available={isEditableGroup(_group.id, currentUser)}
        >
          {_group.name}
        </Link>
      ),
      sorting: { byParameter: ListUserSortableField.groupName },
    },
    {
      title: 'role',
      getValue: ({ role }) => role,
      sorting: { byParameter: ListUserSortableField.role },
    },
    {
      title: 'MFA',
      getValue: ({ mfa, external }) => (external ? '-' : mfa?.totp?.enabled ? 'Configured' : 'Not configured'),
    },
    {
      title: 'Account type',
      // Checkmark
      getValue: ({ external }) => (external ? 'External' : 'Internal'),
    },
    {
      getValue: (user) => (
        <>
          {canEdit(user) && (
            <ActionMenu
              options={[
                {
                  label: 'Edit',
                  icon: <SettingsIcon />,
                  onClick: () => {
                    navigate(routes.usersUpdate({ id: user.id }))
                  },
                },
                ...(canImpersonate(user)
                  ? [
                      {
                        label: 'Impersonate',
                        icon: <SupervisorAccountIcon />,
                        onClick: () => {
                          onImpersonateButtonClicked(user)
                        },
                      },
                    ]
                  : []),
                ...(user.id !== currentUser.id
                  ? [
                      {
                        label: 'Remove',
                        icon: <DeleteIcon />,
                        isDestructive: true,
                        onClick: () => {
                          showConfirmation(
                            () =>
                              void removeUser(user, { dispatch, navigate, userApi: Api.userApi, routes, refreshUsers }),
                            `Are you sure you want to remove user ${user.username}?`,
                          )
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </>
      ),
      props: { align: 'right', noTypography: true },
    },
  ]

  const filters = makeListUsersFilter()
  return (
    <Wrapper
      name="Users"
      actions={
        <>
          {[Role.super, Role.admin].includes(currentUser.role) && (
            <Link key="addUser" id="user-list-component-create-button" type="button" to={routes.usersNew()}>
              <Button variant="contained" color="primary">
                Add user
              </Button>
            </Link>
          )}
        </>
      }
    >
      <Toolbar disableGutters>
        <div style={{ flexShrink: 1, flexGrow: 1 }}>
          <UrlParamFilteredSearchBar
            urlParamCacheKey={'users'}
            mapUrlParamToFilterFn={mapUrlParamToUserFilter}
            mapFilterToUrlParamFn={mapUserFilterToUrlParam}
            searchTokenFilters={filters}
            rawTextFilter={filters.find((f) => f.key === ListUserFilterKey.name)!}
          />
        </div>
      </Toolbar>
      <Table<EnrichedUser>
        emptyMessageComponent={
          error ? (
            <FailedFetchingContent inline message={'Failed to fetch users'} />
          ) : (
            <MissingContent message="No users available" buttonText="Add user" url={routes.usersNew()} />
          )
        }
        config={tableConfig}
        data={users?.items ?? []}
        id="users-table"
        pending={loading}
        pagination={{ total: users?.total ?? 0, useUrlSearchParams: true }}
      />
    </Wrapper>
  )
}
